import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { adSurfaceEnabled } from '../../config/ads';
import { adSlotVisible } from '../../utils/adsEnabled';
import { isValidAdDestination } from '../../utils/adDestinationUrl';
import {
  CarouselRoot,
  SlideLink,
  SlideImage,
  SlideScrim,
  SlideContent,
  SponsorBadge,
  SlideTitle,
  SlideDesc,
  CtaPill,
  NavBtn,
  Dots,
  Dot,
} from './AdCarousel.styled';

export default function AdCarousel({
  ads = [],
  autoPlay = true,
  interval = 5000,
  pageKey,
  onVisibleAd,
  onAdClick,
}) {
  const [current, setCurrent] = useState(0);
  const len = ads.length;

  const next = useCallback(() => {
    if (len <= 1) return;
    setCurrent((c) => (c + 1) % len);
  }, [len]);

  const prev = useCallback(() => {
    if (len <= 1) return;
    setCurrent((c) => (c - 1 + len) % len);
  }, [len]);

  useEffect(() => {
    if (!len) {
      setCurrent(0);
      return;
    }
    setCurrent((c) => Math.min(c, len - 1));
  }, [len]);

  useEffect(() => {
    if (!adSurfaceEnabled() || !autoPlay || len <= 1) return undefined;
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [autoPlay, interval, next, len]);

  const ad = useMemo(() => ads[current], [ads, current]);

  useEffect(() => {
    if (!adSurfaceEnabled() || (pageKey && !adSlotVisible(pageKey)) || !onVisibleAd || !ad) return;
    onVisibleAd(ad);
  }, [pageKey, current, ad?.id, ad?.managedCampaignId, onVisibleAd, ad]);

  if (!adSurfaceEnabled() || (pageKey && !adSlotVisible(pageKey)) || !len || !ads[0]?.image) return null;

  const dest = isValidAdDestination(ad.url);
  const onSlideClick = (e) => {
    if (!dest) e.preventDefault();
    else onAdClick?.(e, ad);
  };

  return (
    <CarouselRoot>
      <SlideLink
        href={dest ? ad.url : '#'}
        onClick={onSlideClick}
        target={dest ? '_blank' : undefined}
        rel={dest ? 'noopener noreferrer sponsored' : undefined}
      >
        <SlideImage src={ad.image} alt={ad.title} loading="lazy" decoding="async" key={ad.id} />
        <SlideScrim />
        <SlideContent>
          <SponsorBadge>Sponsored · {ad.sponsor}</SponsorBadge>
          <SlideTitle>{ad.title}</SlideTitle>
          <SlideDesc>{ad.description}</SlideDesc>
          <CtaPill>
            {ad.cta} <ExternalLink size={12} />
          </CtaPill>
        </SlideContent>
      </SlideLink>

      {len > 1 && (
        <>
          <NavBtn type="button" $side="left" onClick={(e) => { e.preventDefault(); prev(); }} aria-label="Previous ad">
            <ChevronLeft size={18} />
          </NavBtn>
          <NavBtn type="button" $side="right" onClick={(e) => { e.preventDefault(); next(); }} aria-label="Next ad">
            <ChevronRight size={18} />
          </NavBtn>
          <Dots>
            {ads.map((a, i) => (
              <Dot key={a.id || `dot-${i}`} type="button" $active={i === current} onClick={() => setCurrent(i)} aria-label={`Ad ${i + 1}`} />
            ))}
          </Dots>
        </>
      )}
    </CarouselRoot>
  );
}
